import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { getStoredUser } from '../../helpers/user-storage.util';
import { ProposalService } from '../../services/proposal.service';
import { ProjectService } from '../../services/project.service';
import { SiteVisitConfirmationDialogComponent } from '../site-visit-confirmation-dialog/site-visit-confirmation-dialog.component';
import { SiteVisitSendDialogComponent } from '../site-visit-send-dialog/site-visit-send-dialog.component';

@Component({
  selector: 'site-visit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    MatFormFieldModule,
    SiteVisitConfirmationDialogComponent,
    SiteVisitSendDialogComponent,
  ],
  templateUrl: './site-visit.component.html',
  styleUrl: './site-visit.component.scss',
})
export class SiteVisitComponent {
  @Input() projectDetail: any;
  @Input() siteDetail: any;
  @Input() proposal: any;
  @Input() dialogMode: boolean = false;
  @Input() siteType: string = '';
  @Input() siteSubType: string = '';
  @Input() address: any = { streetAddress: '', cityStateZip: '' };

  @Output() setSiteVisit = new EventEmitter<any>();

  user = getStoredUser();
  siteVisitDate1: any;
  siteVisitSlot1: string = '';
  siteVisitDate2: any;
  siteVisitSlot2: string = '';
  siteVisitDate3: any;
  siteVisitSlot3: string = '';
  minDate = new Date();
  maxDate: Date = new Date(9999, 11, 31);

  sendSiteVisitDatesActive: boolean = false;
  siteVisitConfirmationActive: boolean = false;

  constructor(
    private toastr: ToastrService,
    private proposalService: ProposalService,
    private projectService: ProjectService
  ) {}

  ngOnInit(): void {
    if (this.proposal) {
      if (this.proposal.siteVisit1) {
        this.siteVisitDate1 = this.proposal.siteVisit1?.date;
        this.siteVisitSlot1 = this.proposal.siteVisit1?.slot;
      }
      if (this.proposal.siteVisit1) {
        this.siteVisitDate2 = this.proposal.siteVisit2?.date;
        this.siteVisitSlot2 = this.proposal.siteVisit2?.slot;
      }
      if (this.proposal.siteVisit1) {
        this.siteVisitDate3 = this.proposal.siteVisit3?.date;
        this.siteVisitSlot3 = this.proposal.siteVisit3?.slot;
      }
    }
    if (this.siteDetail?.site) {
      this.maxDate = this.siteDetail.site.deliverableDueDate;
    }
  }

  toggleSendModal() {
    this.sendSiteVisitDatesActive = !this.sendSiteVisitDatesActive;
  }

  toggleConfirmationModal() {
    this.siteVisitConfirmationActive = !this.siteVisitConfirmationActive;
  }

  saveSiteVisitDate(priority: number, date: Date) {
    if (this.dialogMode) {
      if (priority === 1) {
        this.setSiteVisit.emit({
          updatedProposal: {
            ...this.proposal,
            siteVisit1: { ...this.proposal.siteVisit1, date: date },
          },
        });
      } else if (priority === 2) {
        this.setSiteVisit.emit({
          updatedProposal: {
            ...this.proposal,
            siteVisit2: { ...this.proposal.siteVisit2, date: date },
          },
        });
      } else if (priority === 3) {
        this.setSiteVisit.emit({
          updatedProposal: {
            ...this.proposal,
            siteVisit3: { ...this.proposal.siteVisit3, date: date },
          },
        });
      }
    }
  }

  saveSiteVisitSlot(priority: number, slot: string) {
    if (priority === 1) {
      this.siteVisitSlot1 = slot;
      if (this.dialogMode) {
        this.setSiteVisit.emit({
          updatedProposal: {
            ...this.proposal,
            siteVisit1: { ...this.proposal.siteVisit1, slot: slot },
          },
        });
      }
    } else if (priority === 2) {
      this.siteVisitSlot2 = slot;
      if (this.dialogMode) {
        this.setSiteVisit.emit({
          updatedProposal: {
            ...this.proposal,
            siteVisit2: { ...this.proposal.siteVisit2, slot: slot },
          },
        });
      }
    } else if (priority === 3) {
      this.siteVisitSlot3 = slot;
      if (this.dialogMode) {
        this.setSiteVisit.emit({
          updatedProposal: {
            ...this.proposal,
            siteVisit3: { ...this.proposal.siteVisit3, slot: slot },
          },
        });
      }
    }
  }

  submitSiteVisitDates() {
    this.toggleSendModal();
    if (this.projectDetail && this.siteDetail && this.proposal) {
      let updatedProposal = this.proposal;
      if (this.siteVisitDate1 && this.siteVisitSlot1) {
        updatedProposal = {
          ...updatedProposal,
          siteVisit1: {
            date: this.siteVisitDate1,
            slot: this.siteVisitSlot1,
            priority: 1,
          },
        };
      }
      if (this.siteVisitDate2 && this.siteVisitSlot2) {
        updatedProposal = {
          ...updatedProposal,
          siteVisit2: {
            date: this.siteVisitDate2,
            slot: this.siteVisitSlot2,
            priority: 2,
          },
        };
      }
      if (this.siteVisitDate3 && this.siteVisitSlot3) {
        updatedProposal = {
          ...updatedProposal,
          siteVisit3: {
            date: this.siteVisitDate3,
            slot: this.siteVisitSlot3,
            priority: 3,
          },
        };
      }
      // saving site visits
      this.proposalService
        .submitSiteVisit(updatedProposal, this.user.id)
        .subscribe((response: any) => {
          const apiResponse: any = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedSiteDetail = {
              ...this.siteDetail,
              proposal: updatedProposal,
            };
            this.siteDetail = updatedSiteDetail;
            const updatedProjectDetail = {
              ...this.projectDetail,
              sites: [updatedSiteDetail],
            };
            this.projectDetail = updatedProjectDetail;
            this.projectService.updateProjectDataSubjectWithSingleSite(
              updatedProjectDetail
            );
            this.toggleConfirmationModal();
          }
        });
    } else {
      this.toastr.error(
        'There was a problem submitting your site visit dates. Please retry when you are ready.'
      );
    }
  }
}
